import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'

import { API__USER } from '../../../api'
import { USER__SET_SUBSCRIPTION, USER__SET_SUBSCRIPTIONS } from '../../../store/actions'

import Modal from '../../../components/Modal'
import { getDate, getShortDate } from '../../../utils/dateTime'
import { ReactComponent as DowngradeIcon } from '../i/downgradeIcon.svg'
import { ReactComponent as SuccessIcon } from '../i/successIcon.svg'
import { BILLING_CYCLE } from '../constants'

const DowngradeModal = ({ open, onClose, onDowngrade, downgradeTo = '', t }) => {
    downgradeTo = downgradeTo.toUpperCase()

    const [disabled, setDisabled] = useState(false)
    const {
        user_details: { id: userId },
        user_subscription: {
            product: { name: productName, billingCycleValue },
            expirationDate,
            productForDowngrade,
            recurringEnabled,
        },
    } = useSelector(state => state)
    const dispatch = useDispatch()

    const currentProduct = productName.split(' ')[0].toUpperCase()
    const date = billingCycleValue === BILLING_CYCLE.year ? getDate(expirationDate) : getShortDate(expirationDate)

    const downgradeAndUpdateSubscription = async (...args) => {
        setDisabled(true)

        onDowngrade(...args)

        dispatch(USER__SET_SUBSCRIPTION(await API__USER.GET_SELECTED_SUBSCRIPTION(userId)))
        dispatch(USER__SET_SUBSCRIPTIONS(await API__USER.GET_SUBSCRIPTIONS(userId)))
    }

    const isSuccess =
        (downgradeTo && productForDowngrade && productForDowngrade.name.split(' ')[0].toUpperCase() === downgradeTo) ||
        recurringEnabled === false

    isSuccess && disabled && setDisabled(false)

    let message
    if (isSuccess) {
        message = downgradeTo ? (
            <Trans i18nKey="You have switched to a {{downgradeTo}} tariff plan." values={{ downgradeTo }} />
        ) : (
            t('You have canceled your subscription.')
        )
    } else {
        message = downgradeTo ? (
            <Trans i18nKey="Are you sure you want to switch to the {{downgradeTo}} plan?" values={{ downgradeTo }} />
        ) : (
            t('Are you sure you want to cancel your subscription?')
        )
    }

    return (
        <Modal
            settings={{ overlay_close: true }}
            data={{ open }}
            events={{ onCloseModal: onClose }}
            classes={{ modal: 'downgrade-modal' }}
        >
            {isSuccess ? (
                <div className="downgrade-modal__main">
                    <h1>{t('Success')}</h1>
                    <div className="downgrade-modal__icon">
                        <SuccessIcon />
                    </div>
                    <p className="downgrade-modal__msg">
                        {message}
                        <br />
                        <Trans i18nKey="Your current plan will be valid until {{date}}." values={{ date }} />
                    </p>
                    <p className="downgrade-modal__ps" />
                    <div className="downgrade-modal__buttons">
                        <button className="button" data-variant="primary" data-size="md" onClick={onClose}>
                            {t('Close')}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="downgrade-modal__main" style={{ opacity: disabled ? 0.7 : 1 }}>
                    <h1>{t('Downgrade')}</h1>
                    <div className="downgrade-modal__icon">
                        <DowngradeIcon />
                    </div>
                    <p className="downgrade-modal__msg">
                        {message}
                        <br />
                        <Trans
                            i18nKey="After downgrade your current plan will be valid until {{date}}."
                            values={{ date }}
                        />
                    </p>
                    <p className="downgrade-modal__ps">
                        <Trans
                            i18nKey="After {{date}} some features of {{currentProduct}} plan will be unavailable"
                            values={{ date, currentProduct }}
                        />
                    </p>
                    <div className="downgrade-modal__buttons">
                        <button
                            className="button"
                            data-variant="primary"
                            data-size="md"
                            onClick={downgradeAndUpdateSubscription}
                            disabled={disabled}
                        >
                            {t('Downgrade')}
                        </button>
                        <button
                            className="button"
                            data-variant="secondary"
                            data-size="md"
                            onClick={onClose}
                            disabled={disabled}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    )
}

export const useDowngradeModal = () => {
    const { t } = useTranslation()

    const [open, setOpen] = useState(false)
    const [downgradeAction, setDowngradeAction] = useState(void 0)
    const [downgradeTo, setDowngradeTo] = useState('Free')

    const modal = (
        <DowngradeModal
            open={open}
            onClose={() => setOpen(false)}
            downgradeTo={downgradeTo}
            onDowngrade={downgradeAction}
            t={t}
        />
    )

    return { modal, setOpen, setDowngradeAction, setDowngradeTo }
}

export default useDowngradeModal
